import React, { useCallback } from 'react'
import Grid from '@mui/material/Grid'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { AsyncSelect, ControlledTextField } from '../../../components/forms'
import { HttpCacheGrupo } from '../../../http/cache/httpCacheGrupo'
import { HttpCacheProfile } from '../../../http/cache/user/profile/httpCacheProfile'
import { HttpGroup } from '../../../http/group/httpGroup'
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, PaisLogoFields } from '../../../types'
import { GroupDTOFields, GroupInsertDTO, GroupViewDTO } from '../../../types/group/groupData'
import { GroupUserFilterDTOFields } from '../../../types/user/userData'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'

type GroupForm = GroupInsertDTO & {
  codPais: number
  codSistema: number
}

interface GroupFormProps {
  group?: GroupViewDTO
  onAfterSubmit: () => void
}

const schemaFiltroUsuarioGrupo = z.object({
  [GroupUserFilterDTOFields.CodsCargo]: z.array(z.number()).optional(),
  [GroupUserFilterDTOFields.Nombre]: z.string().optional(),
  [GroupUserFilterDTOFields.Mail]: z.string().optional(),
  [GroupUserFilterDTOFields.SoloActivos]: z.boolean().optional()
})

export type SchemaFiltroUsuarioGrupo = z.infer<typeof schemaFiltroUsuarioGrupo>

function GroupForm({ group, onAfterSubmit }: GroupFormProps) {
  const { paisLogos } = usePaisLogo()

  const { control, handleSubmit, watch } = useForm<GroupForm>({
    defaultValues: {
      ...group
    }
  })

  const onSubmit = (data: GroupForm) => {
    const groupPromise = group
      ? () => HttpGroup.updateGroup(group[EntityWithIdFields.Id], data)
      : () => HttpGroup.insertGroup(data)

    const promise = groupPromise().then(() => {
      setTimeout(onAfterSubmit, 500)
    })

    toast.promise(promise, {
      success: 'Guardado',
      error: () => 'No se pudo guardar el grupo',
      loading: 'Guardando'
    })

    return promise
  }

  const opcionesSistemaPrincipal = useCallback(
    () =>
      Promise.resolve(paisLogos).then(res =>
        res.map(s => ({
          id: s.id,
          descripcion: s[EntityWithIdAndDescriptionFields.Description],
          icon: <CountryAvatar countryCode={s[PaisLogoFields.CodPais]} />
        }))
      ),
    [paisLogos]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)} id={'group-form'}>
      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={6}>
          <ControlledTextField control={control} name={GroupDTOFields.Name} label='Nombre' fullWidth />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.ProfileId}
            label='Perfil'
            loadOptions={HttpCacheProfile.getProfileList}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodPais}
            fullWidth
            label='Pais'
            loadOptions={opcionesSistemaPrincipal}
          />
        </Grid>
        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodSistema}
            label='Sistema'
            loadOptions={() => HttpCacheWorkflow.getSistemasPorSistemaPrincipal(watch('codPais'))}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.IdGrupoPadre}
            fullWidth
            label='Grupo coordinador'
            loadOptions={() => HttpGroup.getGruposCoordinadores(watch('codPais'), watch('codSistema'))}
          />
        </Grid>

        <Grid item xs={6}>
          <AsyncSelect
            control={control}
            name={GroupDTOFields.CodTipoGrupo}
            fullWidth
            label='Tipo de grupo'
            loadOptions={HttpCacheGrupo.getGrupoTipos}
          />
        </Grid>

        <Grid item xs={6}>
          <ControlledTextField
            multiline
            rows={3}
            control={control}
            name={GroupDTOFields.Observations}
            label='Observaciones'
            fullWidth
          />
        </Grid>
      </Grid>
    </form>
  )
}

export default GroupForm
